/**
 * Date rendering for the teacher subscription surfaces.
 *
 * Extracted from TeacherSubscriptionPage so the date-only parsing rule is unit-testable on its own
 * and cannot silently regress behind a page render. Mirrors `trialDate.ts`, which already solved
 * the identical problem for the trial fields.
 */
/** A bare `YYYY-MM-DD` with no time part — what every LocalDate field on these DTOs serialises to. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Renders a date for display. Handles BOTH shapes this page receives, because they must be parsed
 * differently and getting it wrong is silently off by one.
 *
 * A bare `YYYY-MM-DD` is parsed by the ECMAScript spec as **UTC midnight**, while
 * `toLocaleDateString` renders in local time — so plain `new Date("2026-11-01")` displays
 * "October 31, 2026" in every timezone west of Greenwich. Teachers were shown their subscription
 * start date, end date, pending-change date and every billing period in their history ONE DAY
 * EARLY than the server, the emails and the admin console all report.
 *
 * A calendar date has no time and no zone, so it is parsed as plain digits and constructed in local
 * time. Anything else (a real instant, e.g. `...T10:00:00Z`) carries its own zone and still goes
 * through `Date` unchanged — those were never wrong and must not change.
 *
 * `src/lib/trialDate.ts` solved the same problem for the trial fields and documents it at length;
 * this is that fix applied to the subscription fields, keeping this page's existing long-form
 * output rather than switching to the trial module's "1 Nov 2026" shape.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const dateOnly = DATE_ONLY.exec(iso);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}


export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
